import {
    CheckIcon,
    MagnifyingGlassIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import { Avatar } from "@heroui/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatGroup, Member, ProfileId } from "../../types/types";

export interface AddPeoplePopoverProps {
    isOpen: boolean;
    onClose: () => void;
    onAddPeople: (memberIds: ProfileId[], groupId: number) => Promise<void>;
    onRefresh: () => void;
    getAllMembers: (params: {
        search: string;
        limit: number;
        excludeGroupMembers?: number;
    }) => Promise<{ data: Member[] } | undefined>;
    selectedGroup: ChatGroup | null;
    onViewProfile: (profileId?: string | number | null) => void;
}

export const AddPeoplePopover: React.FC<AddPeoplePopoverProps> = ({
    isOpen,
    onClose,
    onAddPeople,
    onRefresh,
    getAllMembers,
    selectedGroup,
    onViewProfile,
}) => {
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [members, setMembers] = useState<Member[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const popoverRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const { t, i18n } = useTranslation("addPeople");

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (isOpen) {
                fetchMembers();
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchQuery, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setSelectedMembers([]);
            setSearchQuery("");
            setError("");
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const fetchMembers = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await getAllMembers({
                search: searchQuery,
                limit: 20,
                excludeGroupMembers: selectedGroup?.id,
            });
            setMembers(response?.data || []);
        } catch (err) {
            setError("Failed to fetch members. Please try again.");
            console.error("Error fetching members:", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleMemberSelection = (member: Member) => {
        setSelectedMembers((prev) => {
            const isSelected = prev.find((m) => m.id === member.id);
            if (isSelected) {
                return prev.filter((m) => m.id !== member.id);
            } else {
                return [...prev, member];
            }
        });
    };

    const handleAddSelectedMembers = async (event: React.MouseEvent) => {
        event.stopPropagation();
        if (selectedMembers.length === 0 || !selectedGroup) return;

        setError("");
        setLoading(true);
        try {
            const memberIds = selectedMembers.map((member) => member.id);
            await onAddPeople(memberIds, selectedGroup.id);
            await new Promise((resolve) => setTimeout(resolve, 500));
            onRefresh();
            onClose();
        } catch (err) {
            setError("Failed to add members to group. Please try again.");
            console.error("Error adding members:", err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            ref={popoverRef}
            className="bg-white rounded-lg shadow-xl w-96 max-h-[60vh] flex flex-col"
        >
            <div className="flex items-center justify-between p-3 border-b">
                <h3 className="text-md font-semibold text-gray-900">
                    {t("addPeople")}
                </h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>

            <div className="p-3 border-b">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("searchPlaceholder")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto max-h-48">
                {loading ? (
                    <div className="flex items-center justify-center p-6">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-gray-600 text-sm">{t("loading")}</span>
                    </div>
                ) : error ? (
                    <div className="p-3 text-center text-red-600 text-sm">
                        {t("fetchError")}
                    </div>
                ) : members.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">
                        {searchQuery ? t("noPeopleFound") : t("startTyping")}
                    </div>
                ) : (
                    <div className="p-2">
                        {members.map((member) => {
                            const isSelected = selectedMembers.find(
                                (m) => m.id === member.id
                            );
                            return (
                                <div
                                    key={member.id}
                                    onClick={() => toggleMemberSelection(member)}
                                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${isSelected
                                            ? "bg-blue-50 border border-blue-200"
                                            : "hover:bg-gray-50"
                                        }`}
                                >
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onViewProfile(member.profileDto?.id ?? member.id);
                                        }}
                                        className="rounded-full outline-none focus:outline-none focus:ring-0"
                                    >
                                        <Avatar
                                            src={member.profileDto?.profileImage}
                                            name={`${member.profileDto?.firstName} ${member.profileDto?.lastName}`}
                                            size="sm"
                                        />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900 truncate text-left">
                                                {member.profileDto?.firstName}{" "}
                                                {member.profileDto?.lastName}
                                            </span>
                                            <p className="text-xs text-gray-500 truncate text-left">
                                                {member.profileDto?.email}
                                            </p>
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <CheckIcon className="h-4 w-4 text-blue-600" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedMembers.length > 0 && (
                <div className="p-3 border-t bg-gray-50">
                    <p className="text-sm text-gray-600 mb-1">
                        {t("selected")}: {selectedMembers.length} {t("people")}
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {selectedMembers.map((member) => (
                            <span
                                key={member.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                                {member.profileDto?.firstName}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMemberSelection(member);
                                    }}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <XMarkIcon className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-2 p-3 border-t">
                <button
                    onClick={onClose}
                    className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors"
                >
                    {t("cancel")}
                </button>
                <button
                    onClick={handleAddSelectedMembers}
                    disabled={selectedMembers.length === 0 || loading}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${selectedMembers.length > 0 && !loading
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                >
                    {loading
                        ? t("adding")
                        : `${t("addWithCount")} ${selectedMembers.length > 0 ? `(${selectedMembers.length})` : ""
                        }`}
                </button>
            </div>
        </div>
    );
};
